import { QueryKeys } from "@/constants";
import type {
  ICoverageCertificatePayload,
  ICoverageCertificateResponse,
} from "@/model/coverage-certificate";
import type { PlanCoverageInfo } from "@/model/demographics";
import { fetchCoverageCertificate } from "@/services/coverage-certificate";
import type { UseQueryResult } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import useUserInfo from "./use-user-info";
import { queryClient } from "./use-query-client";
import type { GlobalFlag } from "@/model/global-flag";

export const useCoverageCertificate = (
  activeCoverage: PlanCoverageInfo,
): UseQueryResult<ICoverageCertificateResponse, Error> => {
  const userInfo = useUserInfo();
  const showCoverageDocument = (
    queryClient.getQueryData([QueryKeys.GLOBAL_FLAGS, userInfo.username])
      ?.data?.[0] as GlobalFlag
  )?.showCoverageDocument;

  const payload: ICoverageCertificatePayload = {
    divisionId: userInfo?.divisionId,
    employeeId: userInfo?.employeeId,
    groupId: userInfo?.groupId,
    loginGroupId: userInfo?.loginGroupId,
    memberId: userInfo?.memberId,
    planId: activeCoverage.planId ?? userInfo?.planId,
    encrypt: false,
    token: userInfo?.accessToken,
    claimCoverageCode: activeCoverage.claimCoverageCode,
  };

  return useQuery({
    queryKey: [
      QueryKeys.COVERAGE_CERTIFICATE,
      activeCoverage.claimCoverageCode,
    ],
    queryFn: async () => {
      const response = await fetchCoverageCertificate(payload);
      return response;
    },
    enabled: Boolean(userInfo.accessToken) && Boolean(showCoverageDocument),
  });
};
